import type { ReactElement } from 'react';
import { Box, IconButton, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import {
  CheckCircle,
  Close,
  DescriptionOutlined,
  ErrorOutline,
  ExpandLess,
  ExpandMore,
  FileDownloadOutlined,
} from '@mui/icons-material';
import { colors } from 'Constants/Colors';
import { useReportJobs } from 'Hooks/useReportJobs';
import { useReportJobsStore, selectVisibleJobs, isJobActive } from 'stores/report-jobs-store';
import type { ReportJobI } from 'Api/axios/report-jobs-api';

const plural = (count: number, singular: string, many: string) => (count === 1 ? singular : many);

/** Texto del encabezado, en el mismo tono que usa Drive al subir archivos. */
const buildHeading = (jobs: ReportJobI[]) => {
  const active = jobs.filter(isJobActive).length;

  if (active > 0) return `Generando ${active} ${plural(active, 'reporte', 'reportes')}`;

  const failed = jobs.filter(job => job.status === 'error').length;

  if (failed === jobs.length) return `${failed} ${plural(failed, 'reporte falló', 'reportes fallaron')}`;

  const done = jobs.filter(job => job.status === 'done').length;

  return `${done} ${plural(done, 'reporte listo', 'reportes listos')}`;
};

interface JobRowProps {
  job: ReportJobI;
  onDownload(job: ReportJobI): void;
  onDismiss(jobId: string): void;
}

const JobRow = ({ job, onDownload, onDismiss }: JobRowProps) => {
  const active = isJobActive(job);
  const failed = job.status === 'error';

  return (
    <Box display="flex" alignItems="flex-start" gap={1.5} px={2} py={1.25}>
      <DescriptionOutlined sx={{ color: colors.GRAY_DARKER, fontSize: 20, mt: 0.25, flexShrink: 0 }} />

      <Box flex={1} minWidth={0}>
        <Typography
          variant="body2"
          noWrap
          title={job.fileName}
          sx={{ color: colors.BLACK, fontWeight: 500, lineHeight: 1.4 }}
        >
          {job.fileName}
        </Typography>

        {active && (
          <Box mt={0.75}>
            <LinearProgress
              variant={job.totalUnits ? 'determinate' : 'indeterminate'}
              value={job.progress}
              sx={{
                height: 4,
                borderRadius: 2,
                backgroundColor: colors.GRAY_TEXT_FIELD,
                '& .MuiLinearProgress-bar': { backgroundColor: colors.GREEN, borderRadius: 2 },
              }}
            />
            <Typography variant="caption" sx={{ color: colors.GRAY_DARKER, mt: 0.5, display: 'block' }}>
              {job.totalUnits
                ? `${job.progress}% · ${job.processedUnits} de ${job.totalUnits} cursos`
                : 'Preparando el reporte…'}
            </Typography>
          </Box>
        )}

        {failed && (
          <Typography variant="caption" sx={{ color: colors.RED, mt: 0.25, display: 'block' }}>
            {job.errorMessage ?? 'No se pudo generar.'}
          </Typography>
        )}

        {job.status === 'done' && (
          <Typography variant="caption" sx={{ color: colors.GRAY_DARKER, mt: 0.25, display: 'block' }}>
            Listo · {job.totalUnits} {plural(job.totalUnits, 'curso', 'cursos')}
          </Typography>
        )}
      </Box>

      <Box display="flex" alignItems="center" flexShrink={0}>
        {job.status === 'done' && (
          <>
            <CheckCircle sx={{ color: colors.GREEN, fontSize: 20 }} />
            <Tooltip title="Descargar de nuevo">
              <IconButton size="small" onClick={() => onDownload(job)} aria-label="Descargar de nuevo">
                <FileDownloadOutlined sx={{ fontSize: 18, color: colors.GRAY_DARKER }} />
              </IconButton>
            </Tooltip>
          </>
        )}

        {failed && <ErrorOutline sx={{ color: colors.RED, fontSize: 20 }} />}

        {!active && (
          <Tooltip title="Quitar de la lista">
            <IconButton size="small" onClick={() => onDismiss(job.id)} aria-label="Quitar de la lista">
              <Close sx={{ fontSize: 16, color: colors.GRAY_DARK }} />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    </Box>
  );
};

/**
 * Ventana de progreso de los reportes que se generan en segundo plano, al
 * estilo de la que muestra Google Drive al subir archivos.
 *
 * Se monta una sola vez para toda la aplicación: el usuario puede navegar a
 * otra pantalla y seguir viendo el avance. Cuando no hay nada que reportar no
 * pinta nada.
 *
 * Queda por debajo de los diálogos a propósito. Si flotara encima, taparía los
 * botones de la esquina inferior derecha de cualquier modal abierto.
 */
const ReportJobsWidget = (): ReactElement | null => {
  const { download } = useReportJobs();
  const jobs = useReportJobsStore(selectVisibleJobs);
  const collapsed = useReportJobsStore(state => state.collapsed);
  const toggleCollapsed = useReportJobsStore(state => state.toggleCollapsed);
  const dismiss = useReportJobsStore(state => state.dismiss);
  const dismissAll = useReportJobsStore(state => state.dismissAll);

  if (jobs.length === 0) return null;

  const hasActive = jobs.some(isJobActive);

  return (
    <Paper
      elevation={8}
      sx={{
        position: 'fixed',
        bottom: 24,
        right: 24,
        width: 380,
        maxWidth: 'calc(100vw - 32px)',
        borderRadius: 2,
        overflow: 'hidden',
        // Encima del menú lateral (1200), debajo de los diálogos (1300).
        zIndex: 1250,
      }}
    >
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        px={2}
        py={1.25}
        sx={{ backgroundColor: colors.GRAY_HEADER, color: colors.WHITE }}
      >
        <Typography variant="subtitle2" sx={{ flex: 1, fontWeight: 600 }}>
          {buildHeading(jobs)}
        </Typography>

        <IconButton
          size="small"
          onClick={toggleCollapsed}
          sx={{ color: colors.WHITE }}
          aria-label={collapsed ? 'Mostrar detalle' : 'Ocultar detalle'}
        >
          {collapsed ? <ExpandLess sx={{ fontSize: 20 }} /> : <ExpandMore sx={{ fontSize: 20 }} />}
        </IconButton>

        {/* Mientras algo se genera no se puede cerrar: la ventana es la única
            señal de que el proceso sigue vivo. */}
        <Tooltip title={hasActive ? 'No puedes cerrarla mientras se genera un reporte' : 'Cerrar'}>
          <span>
            <IconButton
              size="small"
              onClick={dismissAll}
              disabled={hasActive}
              sx={{ color: colors.WHITE, '&.Mui-disabled': { color: colors.GRAY_DARK } }}
              aria-label="Cerrar"
            >
              <Close sx={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {!collapsed && (
        <Box sx={{ maxHeight: 280, overflowY: 'auto', backgroundColor: colors.WHITE }}>
          {jobs.map(job => (
            <JobRow key={job.id} job={job} onDownload={download} onDismiss={dismiss} />
          ))}
        </Box>
      )}
    </Paper>
  );
};

export default ReportJobsWidget;
